import { varAlpha } from 'minimal-shared/utils';
import { useId, forwardRef, useCallback, useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import { Iconify } from 'src/components/iconify';
import {
  HelperText,
  CaptionText,
  CenteredInput,
  CounterButton,
  InputContainer,
  NumberInputRoot,
} from './styles';

// ----------------------------------------------------------------------

export const NumberInput = forwardRef((props, ref) => {
  const {
    sx,
    error,
    value,
    onChange,
    disabled,
    slotProps,
    helperText,
    captionText,
    hideDivider,
    hideButtons,
    disableInput,
    min = 0,
    max = 9999,
    onIncrementBlocked,
    ...other
  } = props;

  const id = useId();
  const [isFocused, setIsFocused] = useState(false);
  const [draftValue, setDraftValue] = useState('');

  const currentValue = value ?? 0;
  const valueRef = useRef(currentValue);

  useEffect(() => {
    valueRef.current = currentValue;
  }, [currentValue]);

  const displayValue = isFocused ? draftValue : String(currentValue);

  const isDecrementDisabled = currentValue <= min || disabled;
  const isIncrementDisabled = currentValue >= max || disabled;

  const handleDecrement = useCallback(
    (event) => {
      if (valueRef.current <= min || disabled) {
        return;
      }
      const nextValue = valueRef.current - 1;
      valueRef.current = nextValue;
      setDraftValue(String(nextValue));
      onChange?.(event, nextValue, nextValue);
    },
    [disabled, min, onChange]
  );

  const handleIncrement = useCallback(
    (event) => {
      if (valueRef.current >= max || disabled) {
        onIncrementBlocked?.(event);
        return;
      }
      const nextValue = valueRef.current + 1;
      valueRef.current = nextValue;
      setDraftValue(String(nextValue));
      onChange?.(event, nextValue, nextValue);
    },
    [disabled, max, onChange, onIncrementBlocked]
  );

  const handleChange = useCallback(
    (event) => {
      const raw = String(event.target.value ?? '').trim();
      if (raw === '') {
        setDraftValue('');
        onChange?.(event, min);
        return;
      }
      if (!/^\d+$/.test(raw)) {
        return;
      }
      const rawNumeric = raw === '' ? min : Number(raw);
      const transformedValue = transformNumberOnChange(raw, { min, max });
      setDraftValue(String(transformedValue));
      onChange?.(
        event,
        transformedValue,
        Number.isFinite(rawNumeric) ? rawNumeric : transformedValue
      );
    },
    [max, min, onChange]
  );

  const handleInputFocus = useCallback((event) => {
    setIsFocused(true);
    setDraftValue(String(currentValue));
  }, [currentValue]);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    const committed = transformNumberOnChange(draftValue, { min, max });
    setDraftValue(String(committed));
  }, [draftValue, max, min]);

  const handleInputClick = useCallback((event) => {
    event.target.select();
  }, []);

  const handleButtonMouseDown = useCallback((event) => {
    // Keep focus on the input so the first click on +/- still fires onClick (no double-click).
    event.preventDefault();
  }, []);

  const mergeButtonMouseDown = useCallback(
    (slotButtonProps) => (event) => {
      handleButtonMouseDown(event);
      slotButtonProps?.onMouseDown?.(event);
    },
    [handleButtonMouseDown]
  );

  const {
    onFocus: inputOnFocus,
    onBlur: inputOnBlur,
    onClick: inputOnClick,
    ...inputRest
  } = slotProps?.input ?? {};

  const decrementButtonProps = slotProps?.decrementButton ?? slotProps?.button;
  const incrementButtonProps = slotProps?.incrementButton ?? slotProps?.button;

  return (
    <Box {...slotProps?.wrapper}>
      <NumberInputRoot
        ref={ref}
        sx={[
          (theme) => ({
            '--border-color': varAlpha(theme.vars.palette.grey['500Channel'], 0.8),
            '--vertical-divider-color': hideDivider
              ? 'transparent'
              : varAlpha(theme.vars.palette.primary.mainChannel, 0.2),
            '--input-background':
              !disabled && error
                ? varAlpha(theme.vars.palette.error.mainChannel, 0.08)
                : varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        {!hideButtons && (
          <CounterButton
            {...decrementButtonProps}
            disabled={isDecrementDisabled}
            onMouseDown={mergeButtonMouseDown(decrementButtonProps)}
            onClick={handleDecrement}
          >
            <Iconify width={16} icon="mingcute:minimize-line" />
          </CounterButton>
        )}

        <InputContainer {...slotProps?.inputWrapper}>
          <CenteredInput
            name={id}
            disabled={disabled || disableInput}
            value={displayValue}
            onChange={handleChange}
            inputMode="numeric"
            {...inputRest}
            onFocus={(event) => {
              handleInputFocus(event);
              inputOnFocus?.(event);
            }}
            onBlur={(event) => {
              handleInputBlur();
              inputOnBlur?.(event);
            }}
            onClick={(event) => {
              handleInputClick(event);
              inputOnClick?.(event);
            }}
          />

          {captionText && <CaptionText {...slotProps?.captionText}>{captionText}</CaptionText>}
        </InputContainer>

        {!hideButtons && (
          <CounterButton
            {...incrementButtonProps}
            disabled={isIncrementDisabled && !onIncrementBlocked}
            onMouseDown={mergeButtonMouseDown(incrementButtonProps)}
            onClick={handleIncrement}
          >
            <Iconify width={16} icon="mingcute:add-line" />
          </CounterButton>
        )}
      </NumberInputRoot>

      {helperText && (
        <HelperText error={error} {...slotProps?.helperText}>
          {helperText}
        </HelperText>
      )}
    </Box>
  );
});

// ----------------------------------------------------------------------

export function transformNumberOnChange(value, options) {
  const { min = 0, max = 9999 } = options ?? {};

  if (!value || value.trim() === '') {
    return 0;
  }

  const numericValue = Number(value.trim());

  if (!Number.isNaN(numericValue)) {
    // Clamp the value between min and max
    return Math.min(Math.max(numericValue, min), max);
  }

  return 0;
}
